import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, getToken, setToken, clearToken, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    // user states: null = checking, false = unauthenticated, object = authenticated
    const [user, setUser] = useState(null);

    const refresh = useCallback(async () => {
        if (!getToken()) {
            setUser(false);
            return;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch {
            clearToken();
            setUser(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            setToken(data.access_token);
            setUser(data.user);
            return { ok: true };
        } catch (e) {
            const msg = formatApiErrorDetail(e?.response?.data?.detail) || e.message;
            return { ok: false, error: msg };
        }
    };

    const logout = () => {
        clearToken();
        setUser(false);
    };

    return (
        <AuthContext.Provider value={{ user, login, logout, refresh }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
    return ctx;
}
